/**
 * Electrode contact, estimated from the signal itself.
 *
 * The Muse device reports no impedance. A dry electrode off the skin shows up as a
 * flat line, a railed input, very large swings, or a trace dominated by mains
 * hum; a seated one shows ordinary EEG amplitudes. This heuristic reads one
 * second at a time at the native 256 Hz and needs two agreeing seconds before
 * a status changes, so a single blink does not flicker the chips.
 *
 * It is a fit hint for the person setting up, not an impedance measurement.
 */
export type ContactStatus = "good" | "fair" | "poor" | "off" | "unknown";

export type ContactSnapshot = {
  site: string;
  status: ContactStatus;
  /** good 1 · fair 0.66 · poor 0.33 · off/unknown 0 */
  score: number;
  /** 1 Hz high-passed standard deviation over the last second. */
  acRmsUv: number | null;
  /** Mains-band power over 1–40 Hz power. */
  lineRatio: number | null;
  updatedAt: number | null;
};

const SCORES: Record<ContactStatus, number> = { good: 1, fair: 0.66, poor: 0.33, off: 0, unknown: 0 };
const RATE = 256;

export type ContactOptions = {
  mainsHz?: 50 | 60;
  /** Raw values at or beyond these are railed. Muse device µV (Bluetooth): ±724; LibMuse µV: 0 … 1682.8. */
  rails?: [number, number];
};

class ChannelContact {
  #window: number[] = [];
  #status: ContactStatus = "unknown";
  #candidate: ContactStatus | null = null;
  #acRms: number | null = null;
  #lineRatio: number | null = null;
  #updatedAt: number | null = null;

  constructor(readonly site: string) {}

  reset(): void {
    this.#window = [];
    this.#status = "unknown";
    this.#candidate = null;
    this.#acRms = this.#lineRatio = this.#updatedAt = null;
  }

  ingest(values: ArrayLike<number>, now: number, options: Required<ContactOptions>): void {
    for (let i = 0; i < values.length; i++) {
      this.#window.push(values[i]!);
      if (this.#window.length === RATE) {
        this.#evaluate(this.#window, now, options);
        this.#window = [];
      }
    }
  }

  #evaluate(window: number[], now: number, { mainsHz, rails }: Required<ContactOptions>): void {
    const finite = window.filter(Number.isFinite);
    let next: ContactStatus;
    if (finite.length < window.length / 2) {
      next = "off";
      this.#acRms = this.#lineRatio = null;
    } else {
      const railed = finite.filter((value) => value <= rails[0] || value >= rails[1]).length / finite.length;
      const mean = finite.reduce((sum, value) => sum + value, 0) / finite.length;
      // Remove the mean and the slow drift with a 1 Hz one-pole high-pass.
      const a = Math.exp(-2 * Math.PI * 1 / RATE);
      let previousIn = finite[0]! - mean, previousOut = 0, energy = 0;
      const ac = finite.map((value) => {
        const x = value - mean;
        const y = a * (previousOut + x - previousIn);
        previousIn = x;
        previousOut = y;
        energy += y * y;
        return y;
      });
      const std = Math.sqrt(energy / ac.length);
      // 1 Hz DFT bins over the second: mains ±1 Hz against 1–40 Hz.
      const power = (hz: number) => {
        let re = 0, im = 0;
        for (let i = 0; i < ac.length; i++) {
          const angle = 2 * Math.PI * hz * i / RATE;
          re += ac[i]! * Math.cos(angle);
          im -= ac[i]! * Math.sin(angle);
        }
        return re * re + im * im;
      };
      let band = 0;
      for (let hz = 1; hz <= 40; hz++) band += power(hz);
      const mains = power(mainsHz - 1) + power(mainsHz) + power(mainsHz + 1);
      const lineRatio = band > 1e-9 ? mains / band : null;
      this.#acRms = std;
      this.#lineRatio = lineRatio;
      if (std < 0.3 || std > 300 || railed > 0.1) next = "off";
      else if (std > 75 || (lineRatio !== null && lineRatio > 1)) next = "poor";
      else if (std > 40 || (lineRatio !== null && lineRatio > 0.3)) next = "fair";
      else next = "good";
    }
    this.#updatedAt = now;
    if (this.#status === "unknown" || next === this.#status) {
      this.#status = next;
      this.#candidate = null;
    } else if (this.#candidate === next) {
      this.#status = next;
      this.#candidate = null;
    } else {
      this.#candidate = next;
    }
  }

  snapshot(now: number): ContactSnapshot {
    const stale = this.#updatedAt === null || now - this.#updatedAt > 2500;
    const status = stale ? "unknown" : this.#status;
    return { site: this.site, status, score: SCORES[status], acRmsUv: stale ? null : this.#acRms, lineRatio: stale ? null : this.#lineRatio, updatedAt: this.#updatedAt };
  }
}

export class ContactQuality {
  #channels: ChannelContact[];
  #options: Required<ContactOptions>;

  constructor(sites: readonly string[], options: ContactOptions = {}) {
    this.#channels = sites.map((site) => new ChannelContact(site));
    this.#options = { mainsHz: options.mainsHz ?? 50, rails: options.rails ?? [-724, 724] };
  }

  configure(options: ContactOptions): void {
    this.#options = { ...this.#options, ...options };
  }

  reset(): void { for (const channel of this.#channels) channel.reset(); }

  /** One array per site at 256 Hz. */
  ingest(samples: readonly ArrayLike<number>[], now: number): void {
    this.#channels.forEach((channel, index) => channel.ingest(samples[index] ?? [], now, this.#options));
  }

  snapshot(now: number): ContactSnapshot[] { return this.#channels.map((channel) => channel.snapshot(now)); }
}
